import { createFileRoute } from '@tanstack/react-router';

import DirectoryLettingAgentsIndexPage from '@/pages/directory/DirectoryLettingAgentsIndexPage';
import { coveredDirectorySlugs } from '@/pages/directory/utils/directory-listing';
import { getDirectoryAreas } from '@/pages/directory/utils/get-directory-areas';
import { getDirectoryTowns } from '@/sanity/directory';
import { getFooterLandingPages } from '@/sanity/landing-page';
import { getLegalLinks } from '@/sanity/legal-document';
import {
  absoluteUrl,
  buildBreadcrumbListSchema,
  buildPageMeta,
  buildStructuredDataScript,
  itemListStructuredData,
} from '@/utils/seo';

export const Route = createFileRoute('/letting-agents/')({
  loader: async () => {
    const [areas, towns, footerLandingPages, footerLegalLinks] =
      await Promise.all([
        getDirectoryAreas(),
        getDirectoryTowns(),
        getFooterLandingPages(),
        getLegalLinks(),
      ]);

    return { areas, footerLandingPages, footerLegalLinks, towns };
  },
  component: DirectoryLettingAgentsIndexPage,
  head: ({ loaderData }) => {
    const pageMeta = buildPageMeta({
      title: 'Compare letting agents near you — Budgy',
      description:
        'Compare local letting agents, published fees, reviews and services with clear links to every source.',
      path: '/letting-agents',
    });
    const coveredSlugs = coveredDirectorySlugs(loaderData?.towns ?? []);
    const uncoveredAreas = (loaderData?.areas ?? []).filter(
      (area) => !coveredSlugs.has(area.slug),
    );
    const itemList = itemListStructuredData([
      ...(loaderData?.towns ?? []).flatMap((town) =>
        town.slug
          ? [absoluteUrl(`/letting-agents/${encodeURIComponent(town.slug)}`)]
          : [],
      ),
      ...uncoveredAreas.map((area) =>
        absoluteUrl(`/letting-agents/${encodeURIComponent(area.slug)}`),
      ),
    ]);

    return {
      ...pageMeta,
      scripts: [
        buildStructuredDataScript(
          buildBreadcrumbListSchema([
            {
              name: 'Letting agents',
              url: absoluteUrl('/letting-agents'),
            },
          ]),
        ),
        ...(itemList ? [buildStructuredDataScript(itemList)] : []),
      ],
    };
  },
});
